// #########################################################################
//
// This service is used to keep track of all information about the
// canvas as well as all information related to it
//
// #########################################################################

import {TurtleService} from './turtle.service';
import {ColorService} from './color.service';
import {Rectangle} from '../lib/Rectangle';
import {Utils} from '../lib/Utils';
import {SVGImage} from '../lib/SVGImage';
import { Injectable } from '@angular/core';
import { LanguageService } from './language.service';

@Injectable()
export class CanvasService {

  /*
    Can be used to enforce a minimal stroke-width that is larger
    than 1. This value is added onto the pen width specified by
    the user. To the user, this is not visible.
  */
  private readonly PEN_WIDTH_ADDEND: number = 1;

  // Factor between the viewable canvas and the actual dimension
  private readonly BACKGROUND_SIZE_FACTOR: number = 10;

  // Margin that is added to the drawing when downloaded
  private readonly PRINT_MARGIN: number = 10;

  private readonly STROKE_LENGTH_MULTIPLIER = 1 / 1.8;

  // Attributes for SVG drawing
  private readonly SVG_ATTRIBUTES: string = 'stroke-linecap="square" shape-rendering="crispEdges"';

  private img: HTMLImageElement;

  // Rendering contexts of turtle and drawing canvas'
  public contextTurtle: CanvasRenderingContext2D;
  public contextDraw: CanvasRenderingContext2D;
  public contextBackground: CanvasRenderingContext2D;

  // Information about image and canvas dimension
  private canvasWidth;
  private canvasHeight;
  private imageWidth;
  private imageHeight;
  public static initialized = false;

  // Canvas image stored as vector and bitmap
  private SVGDrawing: SVGImage;
  private SVGBackground: SVGImage;
  private bitmapDrawing: HTMLImageElement;
  private bitmapBackground: HTMLImageElement;
  private completeRedraw: boolean;

  // Information about
  private viewport: Rectangle;
  private screenColor: ColorService;

  // An array of [x, y, direction, visibility] tuples, signifying the turtles' positions, directions and visitbility at the point in time the last canvas flush was executed.
  // (currently, the canvas has only one turtle, but as soon as Nils' changes are merged, we can have multiple ones on the canvas)
  private turtleInfoAtLastFlush: any[][] = <any[][]> Array();

  constructor(private turtleService: TurtleService, private lang: LanguageService) {
    this.screenColor = new ColorService("white");
    this.bitmapDrawing = new Image();
    this.bitmapBackground = new Image();

    /*
      Even though everything is done programmatically, we need to
      wait for bitmapImage to finish loading when changing the source
      (see flushImageData())
    */
    this.bitmapDrawing.onload = this.drawImageDrawing.bind(this);
    this.bitmapBackground.onload = this.drawImageBackground.bind(this);
  }

  // Used to set image data from outside (for example by the debugger)
  public getImageData(): string[] {
    return [this.SVGBackground.getSVGSerialized(), this.SVGDrawing.getSVGSerialized()];
  }

  // Provides image data for external use (for example for the debugger)
  public setImageData(imageData: string[]) {
    this.SVGBackground.setSVGFromString(imageData[0]);
    this.SVGDrawing.setSVGFromString(imageData[1]);
    this.completeRedraw = true;
  }

  public setImg(img: HTMLImageElement) {
    this.img = img;
  }

  public getImg(): HTMLImageElement {
    return this.img;
  }

  // Creates a new canvas
  public initializeCanvas(canvasWidth: number, canvasHeight: number): void {
    this.canvasHeight = canvasHeight;
    this.canvasWidth = canvasWidth;
    this.imageHeight = this.BACKGROUND_SIZE_FACTOR * canvasHeight;
    this.imageWidth = this.BACKGROUND_SIZE_FACTOR * canvasWidth;

    // Create empty canvas with centered viewport
    this.clearScreen();
    CanvasService.initialized = true;
  }

  public setCanvasDimension(canvasWidth: number, canvasHeight: number) {
    // Calculate offsets for translation
    let dx = - (canvasWidth - this.canvasWidth) / 2;
    let dy = - (canvasHeight - this.canvasHeight) / 2;

    // Adjust canvas properties
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.viewport.width = canvasWidth;
    this.viewport.height = canvasHeight;

    // Translate canvas to keep up with resize if possible
    if (this.viewport.x + dx > 0 && this.viewport.getRight() + dx < this.imageWidth) {
      this.viewport.x += dx;
    } else if (this.viewport.getRight() + dx > this.imageWidth) {
      this.viewport.x += dx * 2;
    }
    if (this.viewport.y + dy > 0 && this.viewport.getBottom() + dy < this.imageHeight) {
      this.viewport.y += dy;
    } else if (this.viewport.getBottom() + dy > this.imageHeight) {
      this.viewport.y += dy * 2;
    }

    // Changing canvas dimensions clears canvas, thus a complete redraw is needed
    this.completeRedraw = true;
  }

  public getViewedRegionCenterX(): number {
	  return this.viewport.x + this.viewport.width / 2;
  }

  public getViewedRegionCenterY(): number {
	  return this.viewport.y + this.viewport.height / 2;
  }

  public dragCanvas(deltaX: number, deltaY: number): void {
    if (this.isOutsideImageData(this.viewport, deltaX, deltaY)) {
      return;
    }
    this.viewport.x += deltaX;
    this.viewport.y += deltaY;
    this.contextTurtle.clearRect(0, 0, this.viewport.width, this.viewport.height);

    // Dragging canvas changes viewport, need complete redraw
    this.completeRedraw = true;
    this.flushImageData();
  }

  private isOutsideImageData(region: Rectangle, deltaX: number, deltaY: number): boolean {
    return region.x + deltaX < 0
      || region.y + deltaY < 0
      || region.getRight() + deltaX >= this.imageWidth
      || region.getBottom() + deltaY >= this.imageHeight;
  }

  /*
    bitmapImage and backgroundImage have a handler attached
    to them that will update the canvas' once the source is
    changed (see constructor)
  */
  private flushImageData(): void {
    //NOTE: The following line is required for Safari reflush (WebKit needs src to be reset before rewrite)
    this.bitmapDrawing.src = "";

    // Flush background if necessary
    if (this.SVGBackground.hasChanged()) {
      this.bitmapBackground.src = this.SVGBackground.getSVGURI();
    }

    // Sometimes a complete drawing is needed
    if (this.completeRedraw) {
      this.contextDraw.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      this.bitmapDrawing.src = this.SVGDrawing.getSVGURI();
      this.completeRedraw = false;
    }

    // If possible only draw incrementally over previous stuff
    if (this.SVGDrawing.hasChanged()) {
      this.bitmapDrawing.src = this.SVGDrawing.getSVGUpdateURI();
    }
  }

  private drawImageDrawing() {
	  this.contextDraw.drawImage(this.bitmapDrawing, this.viewport.x, this.viewport.y, this.canvasWidth, this.canvasHeight,
		  0, 0, this.canvasWidth, this.canvasHeight);
  }

  private drawImageBackground() {
	  this.contextBackground.drawImage(this.bitmapBackground, 0, 0);
  }

  public storeTurtleInfoForFlush(): void {
    this.turtleInfoAtLastFlush[0] = <number[]> Array();
    this.turtleInfoAtLastFlush[0][0] = this.turtleService.getX();
    this.turtleInfoAtLastFlush[0][1] = this.turtleService.getY();
    this.turtleInfoAtLastFlush[0][2] = this.turtleService.getDirection();
    this.turtleInfoAtLastFlush[0][3] = this.turtleService.getVisibility();
  }

  private clearTurtleBeforeFlush(turtleId: number): void {
    let turtleInfo = this.turtleInfoAtLastFlush[turtleId];
    if (turtleInfo[3]) {
      this.contextTurtle.save();
      this.contextTurtle.translate(turtleInfo[0] - this.viewport.x, turtleInfo[1] - this.viewport.y);
      this.contextTurtle.rotate(turtleInfo[2] * Math.PI / 180);
      this.contextTurtle.clearRect(-this.img.width - 1, -this.img.height - 1, this.img.width * 2 + 2, this.img.height * 2 + 2);
      this.contextTurtle.restore();
    }
  }

  // draw turtle rotated
  public drawTurtle(): void {
    if (this.turtleService.getVisibility()) {
      this.contextTurtle.save();
      this.contextTurtle.translate(this.turtleService.getX() - this.viewport.x, this.turtleService.getY() - this.viewport.y);
      this.contextTurtle.rotate(this.turtleService.getDirection() * Math.PI / 180);
      this.contextTurtle.drawImage(this.img, -this.img.width / 2, -this.img.height / 2, this.img.width, this.img.height);
      this.contextTurtle.restore();
    }
  }

  // remove turtle from canvas
  public clearTurtle(): void {
    if (this.turtleService.getVisibility()) {
      this.contextTurtle.save();
      this.contextTurtle.translate(this.turtleService.getX() - this.viewport.x, this.turtleService.getY() - this.viewport.y);
      this.contextTurtle.rotate(this.turtleService.getDirection() * Math.PI / 180);
      this.contextTurtle.clearRect(-this.img.width / 2 - 1, -this.img.height / 2 - 1, this.img.width + 2, this.img.height + 2);
      this.contextTurtle.restore();
    }
  }

  // A turtle moves from (x1, y1) to (x2, y2) and leaves behind a stroke
  public makeTurtleStroke(x1: number, y1: number, x2: number, y2: number, color: ColorService, penWidth: number) {
    this.SVGDrawing.addLine(x1, y1, x2, y2, 'stroke="' + color.getColorString() + '" stroke-width="' + (penWidth + this.PEN_WIDTH_ADDEND) + '"');
  }

  public makeTurtleCircle(r: number, cx: number, cy: number, color: ColorService, penWidth: number): void {
    this.SVGDrawing.addCircle(cx, cy, r, 'stroke="' + color.getColorString() + '" stroke-width="' + (penWidth + this.PEN_WIDTH_ADDEND) + '" fill="none"');
  }

  public flushCanvas(): void {
    this.flushImageData();
    this.clearTurtleBeforeFlush(0);
    this.storeTurtleInfoForFlush();
    this.drawTurtle();
  }

  public changeScreenColor(screenColor: ColorService): void {
    this.SVGBackground.setBackgroundColor(screenColor.getColorString());
    this.screenColor = screenColor;
  }

  //used in pe() and ppt() for erasing previous commands
  public getBackgroundColor(){
    return this.screenColor;
  }

  public clearScreen(): void {
    // Create blank, white canvas
    this.screenColor = new ColorService("white");
    this.createEmptyCanvas();

    // Recenter canvas viewport
    this.viewport = new Rectangle(this.canvasWidth, this.canvasHeight, this.canvasWidth, this.canvasHeight);
    this.viewport.x = (this.imageWidth - this.canvasWidth) / 2;
    this.viewport.y = (this.imageHeight - this.canvasHeight) / 2;

    // Redraw empty canvas and turtle
	  this.contextTurtle.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
  }

  public washCanvas() {
    this.createEmptyCanvas();
  }

  private createEmptyCanvas() {
    // Construct SVG for background
    this.SVGBackground = new SVGImage(this.imageWidth, this.imageHeight);
    this.SVGBackground.setBackgroundColor(this.screenColor.getColorString());

    // Construct SVG for the actual drawing
    this.SVGDrawing = new SVGImage(this.imageWidth, this.imageHeight, this.SVG_ATTRIBUTES);

    // Flush new canvas' to screen
    this.contextDraw.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    this.flushImageData();
  }

  // Get coordinates for cropped version of image
  private getCroppedImageCoordinates() {
    // Get coordinates of drawing
    const SVGSerializedNew: string = this.SVGDrawing.getSVGSerialized();
    const xRegex: RegExp = /(x1|x2)=(?:")(.*?)(")/g;
    const yRegex: RegExp = /(y1|y2)=(?:")(.*?)(")/g;
    const xCoords: string[] = Utils.regexAllMatches(xRegex, SVGSerializedNew, 2);
    const yCoords: string[] = Utils.regexAllMatches(yRegex, SVGSerializedNew, 2);

    // Get min/max coordinates, check for out of bounds
    let minX: number = -this.PRINT_MARGIN + Math.max(Math.min.apply(null, xCoords), 0);
    let maxX: number =  this.PRINT_MARGIN + Math.min(Math.max.apply(null, xCoords), this.imageWidth);
    let minY: number = -this.PRINT_MARGIN + Math.max(Math.min.apply(null, yCoords), 0);
    let maxY: number =  this.PRINT_MARGIN + Math.min(Math.max.apply(null, yCoords), this.imageHeight);

    // Check if drawing is empty (no coordinates obtained)
    if (xCoords.length === 0) {
      minX = this.imageWidth / 2;
      maxX = this.imageWidth / 2;
      minY = this.imageHeight / 2;
      maxY = this.imageHeight / 2;
    }

    // Calculate drawing dimensions (including small margin)
    const clippingWidth: number = Math.min(maxX - minX, this.imageWidth);
    const clippingHeight: number = Math.min(maxY - minY, this.imageHeight);
    const x1: number = minX - Math.max(this.canvasWidth - clippingWidth, 0) / 2;
    const x2: number = maxX + Math.max(this.canvasWidth - clippingWidth, 0) / 2;
    const y1: number = minY - Math.max(this.canvasHeight - clippingHeight, 0) / 2;
    const y2: number = maxY + Math.max(this.canvasHeight - clippingHeight, 0) / 2;

    return {x1, x2, y1, y2};
  }

  // download canvas as png
  public downloadAsPNG(name: string) {
    const {x1, x2, y1, y2} = this.getCroppedImageCoordinates();

    // Create new canvas and download
    const background: HTMLImageElement = new Image();
    const drawing: HTMLImageElement = new Image();

    background.onload = function() {
      drawing.onload = function() {
        const clippingCanvas = Utils.createCanvasFromImage([background, drawing], x1, y1, x2 - x1, y2 - y1);
        Utils.downloadCanvasPNG(clippingCanvas, name);
      };
      drawing.src = this.SVGDrawing.getSVGURI();
    }.bind(this);
    background.src = this.SVGBackground.getSVGURI();
  }

  public downloadAsSVG(name: string) {
    const {x1, x2, y1, y2} = this.getCroppedImageCoordinates();

    // Create new SVG, cropped and with background
    const downloadSVG = this.SVGDrawing.copy();
    downloadSVG.setBackgroundColor(this.screenColor.getColorString());
    downloadSVG.setViewBox(x1, x2, y1, y2);
    downloadSVG.setWidth(x2 - x1);
    downloadSVG.setHeight(y2 - y1);

    // Download SVG
    const SVGURI: string = downloadSVG.getSVGURI();
    Utils.downloadCanvasSVG(SVGURI, name);
  }

  public deepCloneState(state) {
    const data = state[1];
    const dataCopy = JSON.parse(JSON.stringify(data));

    const turtle = state[0];
    const turtleCopy = new TurtleService();
    for (const key in turtle) {
      if (turtle.hasOwnProperty(key)) {
        turtleCopy[key] = turtle[key];
      }
    }

    const stateCopy = new Array();
    stateCopy.push(turtleCopy);
    stateCopy.push(dataCopy);

    return stateCopy;
  }

  public restoreState(state) {
    const imageData = state.pop();
    const turtle = state.pop();
    this.setImageData(imageData);
    this.turtleService = turtle;
    this.flushCanvas();
  }

  public getState() {
    const state = new Array();
    state.push(this.turtleService);
    state.push(this.getImageData());

    return this.deepCloneState(state);
  }

  public reload(width: number, height: number) {
    this.turtleService.setX(width / 2);
    this.turtleService.setY(height / 2);
    this.drawTurtle();
  }

  public drawTurtleInit() {
    this.turtleService.reset(this.getViewedRegionCenterX(), this.getViewedRegionCenterY(), true);
    this.turtleService.startX = this.getViewedRegionCenterX();
    this.turtleService.startY = this.getViewedRegionCenterY();
  }


  // Move a turtle a certain distance
  private move(distance: number) {
    // Calculate the turtle position after moving.
    distance *= this.STROKE_LENGTH_MULTIPLIER;
    var oldX = this.turtleService.getX();
    var oldY = this.turtleService.getY();
    this.turtleService.setX(oldX + distance * Math.sin(Math.PI / 180 * this.turtleService.getDirection()));
    this.turtleService.setY(oldY - distance * Math.cos(Math.PI / 180 * this.turtleService.getDirection()));

    if (this.turtleService.getPenDown()) {
      this.makeTurtleStroke(oldX, oldY, this.turtleService.getX(), this.turtleService.getY(), this.turtleService.getPenColor(), this.turtleService.getPenWidth());
    }
  }

  fd(distance) {
    this.move(distance);
  }

  bk(distance) {
    this.move(-distance);
  }

  rt(deg) {
    this.turtleService.setDirection(this.turtleService.getDirection() + deg);
  }

  lt(deg) {
    this.turtleService.setDirection(this.turtleService.getDirection() - deg);
  }

  pu() {
    this.turtleService.setPenVisible(false);
  }

  pd() {
    this.turtleService.setPenVisible(true);
  }

  st() {
    this.turtleService.setTurtleVisible(true);
  }

  ht() {
    this.turtleService.setTurtleVisible(false);
  }

  cs() {
    this.turtleService.setPenColor(new ColorService('black'));
    this.clearScreen();
    this.turtleService.reset(this.getViewedRegionCenterX(), this.getViewedRegionCenterY(), true);
  }

  pe() {
    this.turtleService.setErasing(true, this.getBackgroundColor());
  }

  ppt() {
    this.turtleService.setErasing(false, this.getBackgroundColor());
  }

  setpw(width: number) {
    this.turtleService.setPenWidth(width);
  }



  getColor(arg: any): ColorService {
    return new ColorService(arg);
  }

  setscreencolor(color: ColorService) {
    this.changeScreenColor(color);
  }

  setpencolor(color: ColorService) {
    this.turtleService.setPenColor(color);
  }

  setxy(x: number, y: number) {
    //Note: the turtle's position is always centered in the middle of the underlying bitmap.
    //So if we want to change the coordinates (here they are given as additive difference to the acutal underlying coordinated), we need to translate it accordingly.
    this.setx(x);
    this.sety(y);
  }

  setx(x: number) {
    this.turtleService.setX(this.turtleService.startX + x * this.STROKE_LENGTH_MULTIPLIER);
  }

  sety(y: number) {
    this.turtleService.setY(this.turtleService.startY + (-y * this.STROKE_LENGTH_MULTIPLIER));
  }

  home() {
    this.turtleService.reset(this.turtleService.startX, this.turtleService.startY, false);
  }

  circle(radius: number) {
    radius *= this.STROKE_LENGTH_MULTIPLIER;
    if (this.turtleService.getPenDown()) {
      this.makeTurtleCircle(radius, this.turtleService.getX(), this.turtleService.getY(), this.turtleService.getPenColor(), this.turtleService.getPenWidth());
    }
  }

  setheading(direction: number) {
    this.turtleService.setDirection(direction);
  }

}



// WEBPACK FOOTER //
// ./src/app/services/canvas.service.ts